import { useState, useEffect } from 'react';
import { Button } from './Button';
import { getSession } from '../lib/supabase';
import { registerProfile, checkUsernameAvailability } from '../lib/gameClient';

export interface SetUsernameProps {
  onComplete: (username: string) => void;
  onSignOut?: () => void;
}

export function SetUsername({ onComplete, onSignOut }: SetUsernameProps) {
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSession().then((s) => setToken(s?.session?.access_token ?? null));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('Session expired. Please sign in again.');
      return;
    }
    setError(null);
    const trimmed = username.trim().slice(0, 10);
    if (!trimmed) {
      setError('Username is required');
      return;
    }
    if (trimmed.length > 10) {
      setError('Username must be 10 characters or less');
      return;
    }
    const avail = await checkUsernameAvailability(trimmed);
    if (!avail.available) {
      setError(avail.error ?? 'Username is already taken');
      return;
    }
    setLoading(true);
    const result = await registerProfile(token, trimmed);
    setLoading(false);
    if (!result.ok) {
      setError(result.error ?? 'Failed to set username');
      return;
    }
    onComplete(trimmed);
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-surface border border-white/10 text-text placeholder-text-subtle ' +
    'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors';

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-[400px]">
        <div className="rounded-3xl bg-surface border border-white/5 p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-text tracking-tight">Create your username</h1>
            <p className="text-text-muted mt-1.5 text-sm">
              Choose a unique username to complete your account. Max 10 characters. You must set this to continue.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-text text-sm font-medium mb-1.5">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                required
                maxLength={10}
                className={inputClass}
                placeholder="e.g. PokerKing"
              />
            </div>

            {error && (
              <p className="text-error text-sm" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" size="lg" className="w-full" disabled={loading || !token}>
              {loading ? 'Please wait…' : !token ? 'Loading…' : 'Continue'}
            </Button>

            {onSignOut && (
              <p className="mt-4 text-center text-text-muted text-sm">
                Signed in with the wrong account?{' '}
                <button
                  type="button"
                  onClick={onSignOut}
                  className="text-primary font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
                >
                  Sign out
                </button>
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
